/**
 * THE PLAY QUEUE — the playlist playback experience, opened out.
 *
 * What is playing, where it sits in the order, and the order itself. A playlist page
 * lists the playlist; this lists the QUEUE, which is a different thing the moment the
 * playlist is edited mid-playback: the queue is the order playback actually started in,
 * and the transport walks that rather than the current contents of the page. Showing the
 * playlist here instead would put a "3 of 8" beside a list that had since become nine.
 *
 * NOT A SECOND PLAYER. There is no state in here except whether it is expanded. Every
 * control calls a handler the caller wires straight to playerStore, and the position it
 * draws is the same `elapsed` the Studio transport and the full-screen view draw — so
 * pausing here shows as paused there, and seeking here moves the timeline playhead.
 * The seek bar is components/player/SeekBar, the same control the transport uses. That
 * is the whole of "reuse the existing player/audio architecture".
 *
 * COLLAPSED BY DEFAULT, because it is a second view of something already on screen: the
 * row you started is highlighted in the list behind it. Expanding is for the two things
 * the list cannot show — how far into the track you are, and what is coming next.
 *
 * SIMULATED, in exactly the way the rest of the player is: the position comes from a 1s
 * interval, not from audio. The queue, the order and the position are all real state.
 */
import { useState } from 'react';
import { ChevronDown, ListMusic, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import PlayingIndicator from './PlayingIndicator';
import SeekBar from './SeekBar';
import { IconButton, Tooltip } from '../ui';
import { formatTime, parseLength } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface QueuePanelProps {
  /** The queue, in play order. Rendered as-is; index 0 is the first track. */
  songs: Song[];
  /** Where the playhead is in `songs`. Must be a valid index. */
  currentIndex: number;
  /** True while the clock is advancing. Labels the play button. */
  playing: boolean;
  /** Seconds into the current track. */
  elapsed: number;
  /**
   * Playback rate. Passed to the seek bar so its smoothing covers one tick, and shown
   * beside the clock when it is not 1× — a queue playing at double speed should say so
   * on the surface that is showing the queue. Defaults to 1×.
   */
  speed?: number;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  /** Absent at the ends of the queue, which renders the control disabled. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Jumps to a position in the queue, keeping the order. */
  onSelect: (index: number) => void;
}

export default function QueuePanel({
  songs,
  currentIndex,
  playing,
  elapsed,
  speed = 1,
  onTogglePlay,
  onSeek,
  onPrev,
  onNext,
  onSelect,
  totalLabel,
}: QueuePanelProps & { totalLabel: string }) {
  /*
    Expanded or not. Local, and deliberately not in uiStore: it is one boolean about
    one panel on one screen, which is the same rule the share dialog and the
    full-screen player's `open` follow. Nothing else reads it.
  */
  const [open, setOpen] = useState(false);

  const current = songs[currentIndex];
  if (!current) return null;

  const totalSec = parseLength(current.length);

  return (
    <section
      aria-label="Play queue"
      style={{
        background: c.surface1,
        border: `1px solid ${c.accentBorder}`,
        borderRadius: 12,
        marginBottom: 14,
        overflow: 'hidden',
      }}
    >
      {/*
        The summary row, and the thing that opens it. One button across the whole row
        rather than a chevron on the end, so the target is the width of the panel on a
        phone — and `aria-expanded` on it, so the state is announced rather than only
        drawn.
      */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          background: 'none',
          border: 'none',
          padding: '10px 12px',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'Inter, sans-serif',
          color: c.text1,
        }}
      >
        <ListMusic size={14} color={c.accent} style={{ flexShrink: 0 }} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {current.title}
          </span>
          {/* The position in the queue, stated rather than implied by a highlight —
              the row it refers to may be scrolled out of view. */}
          <span
            style={{
              display: 'block',
              fontSize: 11,
              color: c.text2,
              fontFamily: 'IBM Plex Mono, monospace',
            }}
          >
            {currentIndex + 1} of {songs.length} in queue
          </span>
        </span>
        {/*
          The affordance, spelled out — a chevron alone does not say what it opens.

          Kept at every width rather than hidden on a phone. It costs about 60px and the
          title beside it truncates instead, which is the right trade: the title is also
          in the row below and in the queue list, so it is the repeated thing. Hiding
          this would have needed a new `display: none` in the stylesheet, and the audit in
          styles/responsive.test.ts pins that list deliberately — nothing on a narrow
          screen should lose the words that explain a control.
        */}
        {/* text2: this is a control's own label, so it is the last thing that should be
            faded below readable. text3 is 2.84:1 on surface2 — see contrast.test.ts. */}
        <span style={{ fontSize: 11, color: c.text2, flexShrink: 0 }}>
          {open ? 'Hide queue' : 'Show queue'}
        </span>
        <ChevronDown
          size={14}
          color={c.text2}
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
          }}
        />
      </button>

      {open && (
        <div style={{ borderTop: `1px solid ${c.border}`, padding: '12px' }}>
          {/* ---- the transport for the queue ----
              Wraps rather than shrinking its controls away: the seek bar has a min-width
              of its own below, so on a phone the bar and the clock take a second line
              instead of the buttons being squeezed under 44px. No media query needed —
              `flex-wrap` handles every width by itself, which is why this can be inline
              here when the workspace transport's equivalent had to be CSS. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 10,
              flexWrap: 'wrap',
            }}
          >
            <Tooltip label="Previous track">
              <IconButton
                label="Previous track"
                nativeTitle={false}
                onClick={onPrev}
                disabled={!onPrev}
                style={{ width: 32, height: 32, borderRadius: 9 }}
              >
                <SkipBack size={14} color={c.text2} />
              </IconButton>
            </Tooltip>

            {/*
              Named "Pause playback" / "Resume playback" rather than the bare
              "Pause"/"Play" the transport uses, because the track's own row is on this
              same page with a name of its own. Two controls that do the same thing are
              fine; two controls a screen reader cannot tell apart are not.
            */}
            <button
              type="button"
              onClick={onTogglePlay}
              aria-label={playing ? 'Pause playback' : 'Resume playback'}
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: c.accentFill,
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              {playing ? (
                <Pause size={15} color="#1A1300" />
              ) : (
                <Play size={15} color="#1A1300" style={{ marginLeft: 1 }} />
              )}
            </button>

            <Tooltip label="Next track">
              <IconButton
                label="Next track"
                nativeTitle={false}
                onClick={onNext}
                disabled={!onNext}
                style={{ width: 32, height: 32, borderRadius: 9 }}
              >
                <SkipForward size={14} color={c.text2} />
              </IconButton>
            </Tooltip>

            {/* 120px floor, so the bar is still a bar when the row wraps rather than a
                few pixels of ink between two buttons. */}
            <div style={{ flex: 1, minWidth: 120 }}>
              <SeekBar
                value={elapsed}
                duration={totalSec}
                onSeek={onSeek}
                playing={playing}
                speed={speed}
                label={`Seek within ${current.title}`}
              />
            </div>

            <span
              style={{
                fontSize: 11,
                color: c.text2,
                fontFamily: 'IBM Plex Mono, monospace',
                flexShrink: 0,
              }}
            >
              {formatTime(elapsed)} / {totalLabel}
              {/*
                The rate, shown only when it is not the default. At 2× the clock beside it
                is visibly outrunning a wall clock, and without this the readout looks
                broken rather than fast — the position is the one number on this panel a
                speed change makes surprising, so it is the place to say why.
              */}
              {speed !== 1 && <span style={{ color: c.accent }}> · {speed}×</span>}
            </span>
          </div>

          {/* ---- the order ---- */}
          <ol aria-label="Queue order" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {songs.map((song, i) => {
              const isCurrent = i === currentIndex;
              return (
                <li key={`${song.id}-${i}`}>
                  {/*
                    Pressing a row jumps the queue to it rather than playing that one
                    track, which is what keeps this a queue: the tracks after it still
                    follow on. The name says "in the queue" so it cannot be confused
                    with the playlist row of the same song further down the page.
                  */}
                  <button
                    type="button"
                    onClick={() => onSelect(i)}
                    aria-label={`Play ${song.title} — ${i + 1} of ${songs.length} in the queue`}
                    aria-current={isCurrent ? 'true' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      background: isCurrent ? c.accentSoft : 'none',
                      border: 'none',
                      borderRadius: 8,
                      padding: '7px 8px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    {/*
                      The current row carries a glyph rather than its number, so the
                      playhead is findable without relying on the background alone — and
                      while the clock is running that glyph is the app's one playing visual
                      (see PlayingIndicator), the same animated waveform the library row and
                      the transport draw. Paused on this row it falls back to the Play
                      triangle, because a still waveform would claim motion there is none.

                      16px rather than 14: four 2.5px bars with the 2px gaps come to 16, and
                      the width is set for every row so the titles stay aligned as the
                      playhead moves down the list.

                      THIS IS NOT A PLAY BUTTON, which is why the indicator stays here while
                      Batch 6 §13 moved it out of every play/pause control in the app. It is
                      the row's POSITION slot — it prints "3" on every other row — and the
                      row's own press jumps the queue rather than toggling playback. There is
                      no play/pause glyph here for a waveform to have replaced.
                    */}
                    <span
                      style={{
                        width: 16,
                        flexShrink: 0,
                        fontSize: 11,
                        color: isCurrent ? c.accent : c.text3,
                        fontFamily: 'IBM Plex Mono, monospace',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                      aria-hidden="true"
                    >
                      {isCurrent ? (
                        playing ? (
                          <PlayingIndicator seed={song.id} playing />
                        ) : (
                          <Play size={10} color={c.accent} />
                        )
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        color: isCurrent ? c.text1 : c.text2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {song.title}
                    </span>
                    {/* A duration is metadata someone reads, so text2 — theme.ts lists
                        times and costs there by name. The position number above stays
                        text3 because it is aria-hidden decoration: the order is already
                        visible in the list, which is the same call the playlist row's
                        `.lyx-playlist-pos` makes. */}
                    <span
                      style={{
                        fontSize: 11,
                        color: c.text2,
                        fontFamily: 'IBM Plex Mono, monospace',
                        flexShrink: 0,
                      }}
                    >
                      {song.length}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}
